import React from 'react';
import { AlertTriangle, Info, MoreHorizontal } from 'lucide-react';
import { Asset, SupportedCurrency } from '../types';
import { getCurrencySymbol } from '../utils/currency';
import { MarketQuote } from '../services/marketDataService';
import { buildHoldingDisplayRows, CurrencyDisplayMode, selectDisplayValue } from '../utils/holdingDisplay';

interface HoldingsTableProps {
  assets: Asset[];
  selectedCurrency: SupportedCurrency;
  displayCurrencyMode?: CurrencyDisplayMode;
  liveQuotes?: Record<string, MarketQuote>;
  onSelectAsset?: (asset: Asset) => void;
  onEditAsset?: (asset: Asset) => void;
  onDeleteAsset?: (asset: Asset) => void;
  onResolveInstrument?: (asset: Asset) => void;
}

const money = (value: number, currency: string, digits = 2) => {
  const symbol = getCurrencySymbol((currency || 'GBP').toUpperCase());
  return `${symbol}${value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

const pct = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

const CASH_RETURN_HEADER_TOOLTIP =
  'Cash return uses imported dividends received compared with your current remaining holding. If you previously held more shares or imported partial history, this can be much higher than normal dividend yield.';

const CASH_RETURN_VALUE_LABEL = 'Imported dividends received divided by current remaining cost basis.';

const HoldingsTable: React.FC<HoldingsTableProps> = ({
  assets,
  selectedCurrency,
  displayCurrencyMode = 'preferred',
  liveQuotes,
  onSelectAsset,
  onResolveInstrument,
}) => {
  const primaryTextClass = 'text-[color:var(--text-primary)]';
  const secondaryTextClass = 'text-[color:var(--text-secondary)]';
  const rows = buildHoldingDisplayRows({ assets, selectedCurrency, displayCurrencyMode, liveQuotes });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className={`text-xs uppercase tracking-wider border-b border-slate-800 ${secondaryTextClass}`}>
            <th className="p-4 font-medium">Asset</th>
            <th className="p-4 font-medium text-right">Shares</th>
            <th className="p-4 font-medium text-right">Cost basis</th>
            <th className="p-4 font-medium text-right">Current value</th>
            <th className="p-4 font-medium text-right">Dividends <span className="normal-case opacity-70">received</span></th>
            <th className="p-4 font-medium text-right">
              <span className="inline-flex items-center justify-end gap-1" title={CASH_RETURN_HEADER_TOOLTIP} aria-label={CASH_RETURN_HEADER_TOOLTIP}>
                Cash return
                <Info size={12} aria-hidden="true" />
              </span>
            </th>
            <th className="p-4 font-medium text-right">Total profit</th>
            <th className="p-4 font-medium text-right">Daily</th>
            <th className="p-4 font-medium"></th>
          </tr>
        </thead>
        <tbody className="text-sm">
          {rows.map((row) => {
            const asset = row.asset;
            const displayCurrency = row.displayCurrency;
            const costBasisDisplay = selectDisplayValue(row, row.costBasisNative, row.costBasisBase) ?? 0;
            const avgCostDisplay = selectDisplayValue(row, row.avgCostNative, row.avgCostBase) ?? 0;
            const livePriceDisplay = selectDisplayValue(row, row.livePriceNative, row.livePriceBase);
            const currentValueDisplay = selectDisplayValue(row, row.currentValueNative, row.currentValueBase);
            const dividendsReceivedDisplay = selectDisplayValue(row, row.historicDividendsNative, row.historicDividendsBase) ?? 0;
            const dividendReceivedPerShareDisplay = selectDisplayValue(row, row.dividendReceivedPerShareNative, row.dividendReceivedPerShareBase) ?? 0;
            const receivedYieldOnCost = row.displayCurrency === row.nativeCurrency ? row.receivedYieldOnCostPctNative : row.receivedYieldOnCostPctBase;
            const receivedCurrentYield = row.displayCurrency === row.nativeCurrency ? row.receivedCurrentYieldPctNative : row.receivedCurrentYieldPctBase;
            const profit = selectDisplayValue(row, row.profitNative, row.profitBase);
            const returnPct = row.displayCurrency === row.nativeCurrency ? row.profitPctNative : row.profitPctBase;
            const dailyAmount = selectDisplayValue(row, row.dailyNative, row.dailyBase);
            const dailyPct = row.dailyPct;
            const hasWarning = row.warnings.length > 0;
            const warningText = row.warnings.join(' ');

            return (
              <tr
                key={asset.symbol}
                onClick={() => onSelectAsset?.(asset)}
                className={`group hover:bg-black/5 transition-colors border-b border-slate-300 dark:border-slate-800/50 last:border-none ${onSelectAsset ? 'cursor-pointer' : ''}`}
              >
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center font-semibold text-slate-300">
                      {asset.symbol.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className={`text-xs font-semibold ${secondaryTextClass}`}>{asset.symbol}</div>
                      <div className={`font-semibold ${primaryTextClass} inline-flex items-center gap-1`}>
                        {asset.name}
                        {hasWarning && (
                          <span title={warningText} aria-label={warningText} className="inline-flex text-amber-300">
                            <AlertTriangle size={13} />
                          </span>
                        )}
                      </div>
                      {!asset.instrumentId && !asset.dataProviderId && (
                        <button
                          type="button"
                          className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-300 hover:text-amber-200"
                          onClick={(event) => {
                            event.stopPropagation();
                            onResolveInstrument?.(asset);
                          }}
                        >
                          <AlertTriangle size={12} />
                          Unresolved instrument
                        </button>
                      )}
                    </div>
                  </div>
                </td>

                <td className="p-4 text-right">
                  <div className={`text-base font-semibold ${primaryTextClass}`}>{row.quantity.toLocaleString(undefined, { maximumFractionDigits: 4 })}</div>
                  <div className={`text-xs ${secondaryTextClass}`}>shares</div>
                </td>

                <td className="p-4 text-right">
                  <div className={`text-base font-semibold ${primaryTextClass}`}>{money(costBasisDisplay, displayCurrency)}</div>
                  <div className={`text-xs ${secondaryTextClass}`}>{money(avgCostDisplay, displayCurrency)} avg cost</div>
                </td>

                <td className="p-4 text-right">
                  {currentValueDisplay != null && livePriceDisplay != null ? (
                    <>
                      <div className={`text-base font-semibold ${primaryTextClass}`}>{money(currentValueDisplay, displayCurrency)}</div>
                      <div className={`text-xs ${secondaryTextClass}`}>{money(livePriceDisplay, displayCurrency)} live price</div>
                    </>
                  ) : (
                    <div className={`text-xs ${secondaryTextClass}`}>{asset.instrumentId ? 'Quote unavailable' : 'Unresolved instrument'}</div>
                  )}
                </td>

                <td className="p-4 text-right">
                  <div className={`text-base font-semibold ${primaryTextClass} inline-flex items-center justify-end gap-1 w-full`}>
                    {money(dividendsReceivedDisplay, displayCurrency)}
                    {hasWarning && (
                      <span title={warningText} aria-label={warningText} className="text-amber-300">
                        <AlertTriangle size={12} />
                      </span>
                    )}
                  </div>
                  <div className={`text-xs ${secondaryTextClass}`}>{money(dividendReceivedPerShareDisplay, displayCurrency, 4)} per share</div>
                </td>

                <td className="p-4 text-right">
                  <div className={`text-base font-semibold ${primaryTextClass}`} title={CASH_RETURN_VALUE_LABEL} aria-label={CASH_RETURN_VALUE_LABEL}>{receivedYieldOnCost.toFixed(2)}%</div>
                  <div className={`text-xs ${secondaryTextClass}`}>{receivedCurrentYield.toFixed(2)}% of current value</div>
                </td>

                <td className="p-4 text-right">
                  {profit != null && returnPct != null ? (
                    <>
                      <div className={`text-base font-semibold ${profit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {profit >= 0 ? '+' : '-'}{money(Math.abs(profit), displayCurrency)}
                      </div>
                      <div className={`text-xs ${profit >= 0 ? 'text-emerald-500/80' : 'text-rose-500/80'}`}>{pct(returnPct)}</div>
                    </>
                  ) : (
                    <div className="text-base font-semibold text-slate-500">—</div>
                  )}
                </td>

                <td className="p-4 text-right">
                  {dailyAmount != null && dailyPct != null ? (
                    <>
                      <div className={`text-base font-semibold ${dailyAmount >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {dailyAmount >= 0 ? '+' : '-'}{money(Math.abs(dailyAmount), displayCurrency)}
                      </div>
                      <div className={`text-xs ${dailyAmount >= 0 ? 'text-emerald-500/80' : 'text-rose-500/80'}`}>{pct(dailyPct)}</div>
                    </>
                  ) : (
                    <div className="text-base font-semibold text-slate-500">—</div>
                  )}
                </td>

                <td className="p-4 text-right">
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-200 p-1 rounded hover:bg-white/10"
                    onClick={(event) => event.stopPropagation()}
                    aria-label={`Actions for ${asset.symbol}`}
                  >
                    <MoreHorizontal size={16} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default HoldingsTable;
